import { Close } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Modal,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateRatingMutation } from "src/api/services/patientRatingsApi";
import { Task } from "src/api/types";
import { theme } from "src/assets/theme";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx, mobileSx } from "src/utils/styles/modalStyles";

interface Props {
  title: string;
  text: string | null;
  buttonText?: string;
  open: boolean;
  onClose: (o: boolean) => void;
  task: Task;
}

const RateModal = ({ onClose, open, text, title, task }: Props) => {
  const isMobile = useIsMobile();
  const [updateRating] = useUpdateRatingMutation();
  const [rating, setRating] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const error = useSentryLogger("error");
  const { t } = useTranslation();
  const component = `task-modal.${task.task_type.toLowerCase()}`;

  const handleSubmitRating = useCallback(async () => {
    setLoading(true);
    try {
      const updated = await updateRating({
        completed_at: new Date().toISOString(),
        status: "completed",
        score: rating,
        id: task.resource_id,
      });

      if (updated.data != null) {
        setSubmitted(true);
      }
    } catch (err) {
      error(
        "Oops, looks like there an issue trying to submitting this rating",
        err
      );
    } finally {
      setLoading(false);
    }
  }, [error, rating, task.resource_id, updateRating]);

  const handleRating = useCallback((value: number | null) => {
    if (value != null) {
      setRating(value);
    }
  }, []);

  const handleClose = useCallback(async () => {
    try {
      if (!submitted) {
        await updateRating({
          canceled_at: new Date().toISOString(),
          id: task.resource_id,
          status: "canceled",
        });
      }
    } catch (err) {
      error("Error updating task", err);
    } finally {
      onClose(!open);
    }
  }, [error, onClose, open, submitted, task.resource_id, updateRating]);

  return (
    <Modal open={open} disableEnforceFocus>
      <Stack sx={isMobile ? mobileSx : desktopSx} gap={3} width={440}>
        <IconButton onClick={handleClose} sx={{ alignSelf: "end" }}>
          <Close />
        </IconButton>
        {!submitted ? (
          <>
            <Stack gap={3} alignItems={"center"}>
              <Typography variant="h5" fontWeight={600} textAlign="center">
                {title}
              </Typography>
              {text ? (
                <Typography variant="subtitle1" textAlign="center">
                  {text}
                </Typography>
              ) : null}
              <Rating
                precision={0.5}
                size="large"
                value={rating}
                onChange={(_, newValue) => handleRating(newValue)}
                sx={{ color: theme.palette.primary.main, fontSize: "3rem" }}
              />
            </Stack>
            <LoadingButton
              loading={loading}
              onClick={handleSubmitRating}
              disabled={rating === 0}
              size="large"
              sx={{ padding: "10px 20px 10px 20px" }}
            >
              {t(`rating.buttonText`)}
            </LoadingButton>
          </>
        ) : (
          <>
            <Typography variant="h5" fontWeight={600} textAlign="center">
              {t(`${component}.success.title`, "Thank you for your feedback!")}
            </Typography>
            <Button
              onClick={(o) => onClose(!o)}
              size="large"
              sx={{ padding: "10px 20px 10px 20px" }}
            >
              {t(`button.return.dashboard`)}
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
};

export default RateModal;
